import {once} from 'ramda'
import {Dispatch} from 'redux'

import {trackEvent} from '../../../analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Page,
  PerformanceMetric,
  Team,
  TrackEventPayload
} from '../../../analytics/types/Events'
import {logger} from '../../../utils/logging/logger'
import {logTimeEnd} from '../logging/performanceTimer'

interface TimingEventPayloadParams {
  metric: PerformanceMetric | string
  entity: Entity
  team: Team
  duration: number
  page: Page
}

/**
 * Get the payload for a timing event
 * @param params - The parameters object
 * @param params.metric - The metric to track
 * @param params.entity - The entity to track
 * @param params.team - The team to track
 * @param params.duration - The duration of the event
 * @param params.page - The page to track
 */
export const getTrackTimingEventPayload = ({
  metric,
  entity,
  team,
  duration,
  page
}: TimingEventPayloadParams): Omit<TrackEventPayload, 'payload'> => ({
  category: Category.System,
  entity,
  page,
  action: Action.PerformanceMeasured,
  analyticsContext: {
    [AnalyticsContext.PerformanceMeasuredContext]: {
      metric,
      value: duration,
      unit: 'ms'
    }
  },
  team
})

interface TimingEventParams {
  dispatch: Dispatch
  metric: PerformanceMetric | string
  measureStartName: string
  entity: Entity
  page: Page
  team?: Team
  payload?: TrackEventPayload['payload']
  trackAfterClientSideNavigation?: boolean
}

let landingPathname = ''

if (typeof window !== 'undefined') {
  landingPathname = window.location.pathname
}

/**
 * Tracks a timing event for performance measurement
 * @param params - params object
 * @param params.dispatch - Redux dispatch function
 * @param params.metric - Metric to measure from PerformanceMetric enum
 * @param params.measureStartName - Name of the measure to start
 * @param params.entity - Entity associated with the timing event (default: Entity.PaymentPage)
 * @param params.page - Page associated with the timing event
 * @param params.team - Team associated with the timing event (default: Team.Default)
 * @param params.payload - Additional payload to include with the timing event
 * @param params.trackAfterClientSideNavigation - Whether to track after client side navigation
 */
export const trackTimingEvent = ({
  dispatch,
  metric,
  measureStartName,
  entity,
  page,
  team = Team.Default,
  payload,
  trackAfterClientSideNavigation = false
}: TimingEventParams): void => {
  try {
    // Prevent tracking after client side navigation
    // as most metrics only make sense in the initial page load
    if (
      !trackAfterClientSideNavigation &&
      landingPathname !== window.location.pathname
    ) {
      return
    }

    const metricName = `${entity}-${metric}`
    const duration = logTimeEnd(metricName, measureStartName, false)
    if (typeof duration !== 'number' || duration <= 0) return

    const event = getTrackTimingEventPayload({
      metric,
      entity,
      team,
      duration,
      page
    })
    dispatch(trackEvent({...event, payload}))
  } catch (error) {
    logger.error(`Error tracking timing event for ${metric}:`, error)
  }
}

const trackTimingEventOnce = ({
  metric,
  measureStartName,
  entity,
  page,
  team = Team.Default,
  payload
}: Omit<TimingEventParams, 'dispatch'>) =>
  once((dispatch: Dispatch): void => {
    trackTimingEvent({
      dispatch,
      metric,
      measureStartName,
      entity,
      page,
      team,
      payload
    })
  })

/**
 * Tracks when the payment page JS loaded and skeleton is shown to start making API calls
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackPaymentPageFRP = trackTimingEventOnce({
  metric: PerformanceMetric.FRP,
  measureStartName: 'responseStart',
  entity: Entity.Skeleton,
  page: Page.Payment
})

/**
 * Tracks when Payment Page offer is loaded
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackPaymentPageOfferMRP = trackTimingEventOnce({
  metric: PerformanceMetric.MRP,
  measureStartName: 'responseStart',
  entity: Entity.Offer,
  page: Page.Payment
})

/**
 * Tracks when the Accommodation page loaded and skeleton is shown to start making API calls
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackAccommodationPageFRP = trackTimingEventOnce({
  metric: PerformanceMetric.FRP,
  measureStartName: 'responseStart',
  entity: Entity.Skeleton,
  page: Page.Accommodation
})

/**
 * Tracks when Accommodation page selected deal card is shown
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackAccommodationPageSelectedDealMRP = trackTimingEventOnce({
  metric: PerformanceMetric.MRP,
  measureStartName: 'responseStart',
  entity: Entity.SelectedDeal,
  page: Page.Accommodation
})

/**
 * Tracks when Homepage is shown
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackHomePageMRP = trackTimingEventOnce({
  metric: PerformanceMetric.MRP,
  measureStartName: 'responseStart',
  entity: Entity.HomePage,
  page: Page.Home
})

/**
 * Tracks when SRP page has at least one offer
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackSearchResultsPageMRP = trackTimingEventOnce({
  metric: PerformanceMetric.MRP,
  measureStartName: 'responseStart',
  entity: Entity.Offer,
  page: Page.SearchResults
})

/**
 * Tracks when SRP page skeleton is shown
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackSearchResultsPageFRP = trackTimingEventOnce({
  metric: PerformanceMetric.FRP,
  measureStartName: 'responseStart',
  entity: Entity.Skeleton,
  page: Page.SearchResults
})

/**
 * Tracks when CP page skeleton is shown
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackConfirmationPageFRP = trackTimingEventOnce({
  metric: PerformanceMetric.FRP,
  measureStartName: 'responseStart',
  entity: Entity.Skeleton,
  page: Page.Confirmation
})

/**
 * Tracks when CP offer details are shown
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackConfirmationPageOfferMRP = trackTimingEventOnce({
  metric: PerformanceMetric.MRP,
  measureStartName: 'responseStart',
  entity: Entity.Offer,
  page: Page.Confirmation
})

/**
 * Tracks when BMP page skeleton is shown
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackBookingManagementPageFRP = trackTimingEventOnce({
  metric: PerformanceMetric.FRP,
  measureStartName: 'responseStart',
  entity: Entity.Skeleton,
  page: Page.Itinerary,
  team: Team.Checkout
})

/**
 * Tracks when BMP offer details are shown
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackBookingManagementPageMRP = trackTimingEventOnce({
  metric: PerformanceMetric.MRP,
  measureStartName: 'responseStart',
  entity: Entity.Offer,
  page: Page.Itinerary,
  team: Team.Checkout
})

/**
 * Tracks when MBP page skeleton is shown
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackMyBookingsPageFRP = trackTimingEventOnce({
  metric: PerformanceMetric.FRP,
  measureStartName: 'responseStart',
  entity: Entity.Skeleton,
  page: Page.BookingHistoryPage,
  team: Team.Checkout
})

/**
 * Tracks when BMP content is shown
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const trackMyBookingsMRP = trackTimingEventOnce({
  metric: PerformanceMetric.MRP,
  measureStartName: 'responseStart',
  entity: Entity.BookingHistoryPage,
  page: Page.BookingHistoryPage,
  team: Team.Checkout
})
